/**
 * Customer Authentication Context
 * Handles customer signup, login, and authentication for Rainbow Sails.
 * Uses Mango backend (customer-auth API) - all UI is Rainbow Sails branded.
 * Email/password only; customers use their account to log into the shop.
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import {
  API_ENDPOINTS,
  API_BASE_URL,
  MANGO_SSO_URL,
  resolveShopBusinessId,
} from '../config/api';

const MANGO_TOKEN_KEY = 'mango_token';

const CustomerAuthContext = createContext();

const assertTenantConfigured = async () => {
  const id = await resolveShopBusinessId();
  if (!id) {
    throw new Error(
      'Customer auth is not configured. Missing tenant: set REACT_APP_BUSINESS_ID or map this domain to a business in the backend.'
    );
  }
  return id;
};

/** Decode JWT payload (no verify) — used to avoid 401 noise for non-personal tokens */
const readJwtPayload = (token) => {
  try {
    const parts = String(token).split('.');
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    if (typeof atob !== 'function') return null;
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
};

/** True when a JWT is missing/invalid or past its `exp` (avoids "logged in" UI with a dead token). */
const isJwtExpired = (token) => {
  const p = readJwtPayload(token);
  if (!p) return true;
  if (!p.exp) return false;
  return Date.now() / 1000 > Number(p.exp);
};

/** Mango PersonalAccount id when shop JWT is personal or linked customer (calendar-aware booking). */
const getPersonalAccountIdFromShopToken = (token) => {
  const p = readJwtPayload(token);
  if (!p) return null;
  if (p.type === 'personal') return p.id;
  if (p.type === 'customer' && p.personalAccountId) return p.personalAccountId;
  return null;
};

/** After Mango SSO, create/link CRM Customer and switch to customer JWT when supported */
const linkMangoSessionToShopCustomer = async (mangoToken, setToken, setCustomer) => {
  try {
    const payload = readJwtPayload(mangoToken);
    if (!payload || payload.type !== 'personal') return;
    if (payload.exp && Date.now() / 1000 > Number(payload.exp)) return;

    const businessId = await resolveShopBusinessId();
    if (!businessId) return;
    const res = await fetch(API_ENDPOINTS.customerAuth.sessionFromPersonal, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${mangoToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ businessId }),
    });
    if (!res.ok) return;
    const data = await res.json();
    if (!data?.token || !data?.customer) return;
    localStorage.removeItem(MANGO_TOKEN_KEY);
    localStorage.setItem('customer_token', data.token);
    localStorage.setItem('customer_data', JSON.stringify(data.customer));
    setToken(data.token);
    setCustomer(data.customer);
  } catch {
    // Keep Mango personal session if linking fails (e.g. wrong API host)
  }
};

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Resolve user from Mango token (main auth /auth/me)
  const resolveMangoUser = async (mangoToken) => {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${mangoToken}` },
      });
      if (!res.ok) return null;
      const data = await res.json();
      const u = data.user || data.account;
      if (!u) return null;
      return {
        id: u.id,
        mangoId: u.mangoId,
        email: u.email,
        firstName: u.firstName || u.email?.split('@')[0] || '',
        lastName: u.lastName || '',
        tier: u.tier,
        personalAccountId: u.id,
      };
    } catch {
      return null;
    }
  };

  // Load customer/token on mount and handle SSO return (#mango_token=...)
  useEffect(() => {
    const hash = window.location.hash || '';
    const match = hash.match(/#mango_token=([^&]+)/);
    if (match) {
      const mangoToken = decodeURIComponent(match[1]);
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      localStorage.setItem(MANGO_TOKEN_KEY, mangoToken);
      localStorage.removeItem('customer_token');
      localStorage.removeItem('customer_data');
      resolveMangoUser(mangoToken).then((user) => {
        setToken(mangoToken);
        setCustomer(user);
        if (user) localStorage.setItem('customer_data', JSON.stringify(user));
        setIsLoading(false);
        void linkMangoSessionToShopCustomer(mangoToken, setToken, setCustomer);
      });
      return;
    }

    const savedToken = localStorage.getItem('customer_token');
    const mangoToken = localStorage.getItem(MANGO_TOKEN_KEY);
    const savedCustomer = localStorage.getItem('customer_data');

    // Prefer a valid shop customer_token. A stale Mango token must never override it (that caused
    // 401s on reload where the UI showed "logged in" from cached customer_data but every API failed).
    if (savedToken && !isJwtExpired(savedToken)) {
      setToken(savedToken);
      if (savedCustomer) {
        try {
          setCustomer(JSON.parse(savedCustomer));
        } catch {
          // ignore corrupt cache; refreshCustomer can repopulate
        }
      }
      // Drop any leftover Mango token so it can't win on a later reload.
      if (mangoToken) localStorage.removeItem(MANGO_TOKEN_KEY);
      setIsLoading(false);
      return;
    }

    if (mangoToken && !isJwtExpired(mangoToken)) {
      setToken(mangoToken);
      if (savedCustomer) {
        try {
          setCustomer(JSON.parse(savedCustomer));
        } catch {
          resolveMangoUser(mangoToken).then((user) => {
            setCustomer(user);
            if (user) localStorage.setItem('customer_data', JSON.stringify(user));
          });
        }
      } else {
        resolveMangoUser(mangoToken).then((user) => {
          setCustomer(user);
          if (user) localStorage.setItem('customer_data', JSON.stringify(user));
        });
      }
      void linkMangoSessionToShopCustomer(mangoToken, setToken, setCustomer);
      setIsLoading(false);
      return;
    }

    // Nothing valid: clear stale/expired tokens so we don't fire doomed authenticated requests.
    if (savedToken) localStorage.removeItem('customer_token');
    if (mangoToken) localStorage.removeItem(MANGO_TOKEN_KEY);
    setIsLoading(false);
  }, []);

  // Signup
  const signup = async (userData) => {
    setIsLoading(true);
    try {
      const tenantId = await assertTenantConfigured();

      const response = await fetch(API_ENDPOINTS.customerAuth.signup, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...userData,
          businessId: tenantId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Signup failed');
      }

      // Store token and customer data
      setToken(data.token);
      setCustomer(data.customer);
      localStorage.setItem('customer_token', data.token);
      localStorage.setItem('customer_data', JSON.stringify(data.customer));

      return data.customer;
    } catch (error) {
      console.error('Signup failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Code-only signup ("Secret Shop" community-of-trust flow): a business-issued group
  // code + first name + phone + password, no email. Backend creates a placeholder-email Customer
  // and returns a normal customer JWT, so ordering/messaging work exactly like any
  // other account from here on.
  const signupWithCode = async (code, firstName, phone, password) => {
    setIsLoading(true);
    try {
      const tenantId = await assertTenantConfigured();

      const response = await fetch(API_ENDPOINTS.secretShop.signup, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          businessId: tenantId,
          code,
          firstName,
          phone,
          password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'That code did not work');
      }

      setToken(data.data.token);
      setCustomer(data.data.customer);
      localStorage.setItem('customer_token', data.data.token);
      localStorage.setItem('customer_data', JSON.stringify(data.data.customer));

      return data.data.customer;
    } catch (error) {
      console.error('Code signup failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // "Log back in" for a returning member: phone + password — no new code needed.
  const loginWithPhone = async (phone, password) => {
    setIsLoading(true);
    try {
      const tenantId = await assertTenantConfigured();

      const response = await fetch(API_ENDPOINTS.secretShop.loginByPhone, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ businessId: tenantId, phone, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Wrong phone number or password');
      }

      setToken(data.data.token);
      setCustomer(data.data.customer);
      localStorage.setItem('customer_token', data.data.token);
      localStorage.setItem('customer_data', JSON.stringify(data.data.customer));

      return data.data.customer;
    } catch (error) {
      console.error('Phone login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Login
  const login = async (email, password) => {
    setIsLoading(true);
    try {
      const tenantId = await assertTenantConfigured();

      const response = await fetch(API_ENDPOINTS.customerAuth.login, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          businessId: tenantId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store token and customer data
      setToken(data.token);
      setCustomer(data.customer);
      localStorage.setItem('customer_token', data.token);
      localStorage.setItem('customer_data', JSON.stringify(data.customer));

      return data.customer;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout
  const logout = () => {
    setCustomer(null);
    setToken(null);
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_data');
    localStorage.removeItem(MANGO_TOKEN_KEY);
  };

  const getAuthHeaders = useCallback(() => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const getSignInWithMangoUrl = useCallback(() => {
    const returnUrl = window.location.origin + (window.location.pathname || '') + (window.location.search || '');
    return `${MANGO_SSO_URL}/sso?return_url=${encodeURIComponent(returnUrl)}`;
  }, []);

  // Refresh customer data
  const refreshCustomer = async () => {
    if (!token) return null;

    try {
      const response = await fetch(API_ENDPOINTS.customerProfile.me, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customer data');
      }

      const data = await response.json();
      setCustomer(data.customer);
      localStorage.setItem('customer_data', JSON.stringify(data.customer));

      return data.customer;
    } catch (error) {
      console.error('Failed to refresh customer:', error);
      // If token is invalid, logout
      if (error.message.includes('401') || error.message.includes('403')) {
        logout();
      }
      return null;
    }
  };

  // Hydrate the full profile (incl. shippingAddress) once a shop-customer JWT is present.
  // login()/signup()/session-from-personal all return a slim customer object (no address —
  // see backend customer-auth.routes.ts) so without this, checkout's "use saved address"
  // autofill silently has nothing to fill until the customer happens to open Profile first.
  // This covers fresh logins, signups, SSO linking, and cached-session reloads in one place.
  useEffect(() => {
    if (!token) return;
    const payload = readJwtPayload(token);
    if (!payload || payload.type !== 'customer') return; // Mango-only token, not linked yet
    refreshCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const personalAccountId = useMemo(() => {
    if (!token) return null;
    const fromJwt = getPersonalAccountIdFromShopToken(token);
    if (fromJwt) return fromJwt;
    return customer?.personalAccountId || null;
  }, [token, customer]);

  const hasMangoPersonalCalendar = !!personalAccountId;

  return (
    <CustomerAuthContext.Provider
      value={{
        customer,
        token,
        isLoading,
        isAuthenticated: !!customer && !!token,
        personalAccountId,
        hasMangoPersonalCalendar,
        signup,
        signupWithCode,
        loginWithPhone,
        login,
        logout,
        getAuthHeaders,
        refreshCustomer,
        getSignInWithMangoUrl,
      }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
  }
  return context;
};

